import React, { useMemo, useState } from 'react';
import { useDeals } from '../context/DataContext';
import { useBrandPitches } from '../context/BrandPitchesContext';
import { useAuth } from '../context/AuthContext';
import { Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from './ui/command';
import InitialsAvatar from './InitialsAvatar';
import { PitchRow } from './PitchRow';

/**
 * BrandPitchTalentsList — for a single brand, show which talents are pitching it
 * (with their status, stars, notes). Manager can add talents to the pitch list.
 */
const BrandPitchTalentsList = ({ brandId }) => {
  const { user } = useAuth();
  const { talent } = useDeals();
  const { forBrand, ensurePitch, updatePitch, removePitch, addComms, removeComms } = useBrandPitches();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const [addOpen, setAddOpen] = useState(false);
  const [q, setQ] = useState('');

  const rows = forBrand(brandId);
  // Sort: highest stars first, then converted to bottom, then by created_at
  const sortedRows = useMemo(() => [...rows].sort((a, b) => {
    if ((b.stars || 0) !== (a.stars || 0)) return (b.stars || 0) - (a.stars || 0);
    return 0;
  }), [rows]);

  const talentMap = useMemo(() => Object.fromEntries(talent.map((t) => [t.user_id, t])), [talent]);

  const talentsWithoutPitch = useMemo(() => {
    const taken = new Set(rows.map((r) => r.talent_id));
    return talent.filter((t) => !taken.has(t.user_id));
  }, [talent, rows]);

  const addTalentToPitch = async (talent_id) => {
    await ensurePitch(brandId, talent_id);
    setAddOpen(false); setQ('');
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="brand-pitch-talents-list">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-700">pitching this brand</h3>
        <span className="text-xs text-slate-400">— per-talent status, stars & notes</span>
        {isManager && (
          <Popover open={addOpen} onOpenChange={setAddOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" className="ml-auto bg-pink-500 hover:bg-pink-600 text-white" data-testid="add-talent-pitch-btn">
                <Plus className="w-4 h-4 mr-1" /> add talent
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="p-0 w-80">
              <Command>
                <CommandInput value={q} onValueChange={setQ} placeholder="search talent…" />
                <CommandList>
                  <CommandEmpty>no talent match</CommandEmpty>
                  <CommandGroup>
                    {talentsWithoutPitch.map((t) => (
                      <CommandItem key={t.user_id} value={t.name} onSelect={() => addTalentToPitch(t.user_id)}>
                        <span className="inline-flex items-center gap-2">
                          <InitialsAvatar name={t.name} size={20} />
                          <span>{t.name}</span>
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {sortedRows.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400">
          {isManager ? 'no talents pitching this brand yet — click "add talent" to start' : 'nothing here yet'}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {sortedRows.map((row) => {
            const t = talentMap[row.talent_id];
            const leftContent = (
              <>
                {t ? <InitialsAvatar name={t.name} size={32} /> : <div className="w-8 h-8 rounded-full bg-slate-100" />}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800 truncate">{t?.name || 'unknown talent'}</div>
                </div>
              </>
            );
            return (
              <PitchRow
                key={row.id}
                row={row}
                leftContent={leftContent}
                canEdit={isManager}
                onStars={(v) => updatePitch(row.id, { stars: v })}
                onStatus={(v) => updatePitch(row.id, { status: v })}
                onRemove={() => { if (window.confirm('Remove this talent from the pitch list for this brand?')) removePitch(row.id); }}
                onAddComms={(text) => addComms(row.id, text)}
                onRemoveComms={(cid) => removeComms(row.id, cid)}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default BrandPitchTalentsList;
